'use client';
import React, { useState, useEffect } from 'react';
import { RunwayWindAnalysis } from '@/lib/types';
import { buildRunwaySvgGeometry } from '@/lib/runwayProjection';
import { CYRC_RUNWAYS } from '@/lib/runwayData';

interface RunwayPanelProps {
  analyses: RunwayWindAnalysis[];
  recommendedRunway: string;
  windDirection: number;
  windSpeed: number;
  compact?: boolean;
}

export default function RunwayPanel({
  analyses,
  recommendedRunway,
  windDirection,
  windSpeed,
  compact = false,
}: RunwayPanelProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  // Use a large fixed viewBox so the diagram has plenty of room
  // The SVG will be rendered at 100% width of its container, scaling naturally
  const vbW = compact ? 260 : 340;
  const vbH = compact ? 220 : 290;

  // Generous margin so labels are never clipped
  const margin = compact ? 34 : 44;

  const geometries = buildRunwaySvgGeometry(vbW, vbH, margin, CYRC_RUNWAYS);

  const isRec = (name: string) => name === recommendedRunway;

  // Compute geometric centroid of all runway threshold points
  const allPts = geometries.flatMap((g) => [g.ptA, g.ptB]);
  const windCx = allPts.reduce((s, p) => s + p.x, 0) / allPts.length;
  const windCy = allPts.reduce((s, p) => s + p.y, 0) / allPts.length;

  // Wind arrow: points FROM wind direction (arrow shows where wind is going)
  const windRad = ((windDirection - 180 + 360) % 360) * (Math.PI / 180);
  const windArrowLen = compact ? 24 : 30;

  const wax = windCx + windArrowLen * Math.sin(windRad);
  const way = windCy - windArrowLen * Math.cos(windRad);

  // Arrowhead
  const headSz = compact ? 6 : 7;
  const ha1 = windRad + 2.5;
  const ha2 = windRad - 2.5;
  const hx1 = wax - headSz * Math.sin(ha1);
  const hy1 = way + headSz * Math.cos(ha1);
  const hx2 = wax - headSz * Math.sin(ha2);
  const hy2 = way + headSz * Math.cos(ha2);

  // Label nudge per threshold — push labels away from runway ends
  const labelNudge: Record<string, { dx: number; dy: number }> = {
    '06': { dx: -12, dy: -8 },
    '24': { dx:  12, dy:  8 },
    '12': { dx: -12, dy:  8 },
    '30': { dx:  14, dy: -6 },
    '18': { dx:  12, dy:  8 },
    '36': { dx: -12, dy: -8 },
  };

  // "VENT DU" button position: top-right area of the diagram, inside the SVG
  const ventBtnX = compact ? vbW - 6 : vbW - 8;
  const ventBtnY = compact ? vbH * 0.28 : vbH * 0.28;
  const ventBtnW = compact ? 88 : 108;
  const ventBtnH = compact ? 18 : 22;
  const ventFontSize = compact ? 8 : 9.5;

  return (
    <div className="flex flex-col items-center w-full h-full">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${vbW} ${vbH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: 'visible', display: 'block' }}
        suppressHydrationWarning
      >
        <defs>
          <radialGradient id="runwayPanelBg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F1F5F9" />
            <stop offset="100%" stopColor="#E2E8F0" />
          </radialGradient>
          <filter id="runwayWindGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="2.5" result="blur"/>
            <feMerge>
              <feMergeNode in="blur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Background */}
        <rect
          x="2" y="2"
          width={vbW - 4} height={vbH - 4}
          rx="10"
          fill="url(#runwayPanelBg)"
          stroke="#CBD5E1"
          strokeWidth="1"
        />

        {/* Runways */}
        {geometries.map((rwy) => {
          const recA = isRec(rwy.runwayA);
          const recB = isRec(rwy.runwayB);
          const anyRec = recA || recB;
          const color = anyRec ? '#16A34A' : '#94A3B8';
          const strokeW = Math.max(4, rwy.svgWidth * (compact ? 0.8 : 1));

          const dx = rwy.ptB.x - rwy.ptA.x;
          const dy = rwy.ptB.y - rwy.ptA.y;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;
          const ux = dx / len;
          const uy = dy / len;

          const nudgeA = labelNudge[rwy.runwayA] ?? { dx: 0, dy: 0 };
          const nudgeB = labelNudge[rwy.runwayB] ?? { dx: 0, dy: 0 };

          // labelOff: distance along runway axis beyond the threshold point
          const labelOff = compact ? 13 : 16;
          const lxA = rwy.ptA.x - ux * labelOff + nudgeA.dx;
          const lyA = rwy.ptA.y - uy * labelOff + nudgeA.dy;
          const lxB = rwy.ptB.x + ux * labelOff + nudgeB.dx;
          const lyB = rwy.ptB.y + uy * labelOff + nudgeB.dy;

          const perpW = strokeW + 2;

          return (
            <g key={rwy.id}>
              {/* Recommended runway glow */}
              {anyRec && (
                <line
                  x1={rwy.ptA.x} y1={rwy.ptA.y}
                  x2={rwy.ptB.x} y2={rwy.ptB.y}
                  stroke="rgba(22,163,74,0.3)"
                  strokeWidth={strokeW + 8}
                  strokeLinecap="round"
                />
              )}
              {/* Runway surface */}
              <line
                x1={rwy.ptA.x} y1={rwy.ptA.y}
                x2={rwy.ptB.x} y2={rwy.ptB.y}
                stroke={color}
                strokeWidth={strokeW}
                strokeLinecap="round"
              />
              {/* Center dashes */}
              <line
                x1={rwy.ptA.x} y1={rwy.ptA.y}
                x2={rwy.ptB.x} y2={rwy.ptB.y}
                stroke="white"
                strokeWidth="1.5"
                strokeDasharray="6 5"
                strokeLinecap="round"
              />
              {/* Threshold bar A */}
              <line
                x1={rwy.ptA.x - uy * perpW} y1={rwy.ptA.y + ux * perpW}
                x2={rwy.ptA.x + uy * perpW} y2={rwy.ptA.y - ux * perpW}
                stroke={recA ? '#16A34A' : '#94A3B8'}
                strokeWidth={recA ? 2.5 : 1.5}
              />
              {/* Threshold bar B */}
              <line
                x1={rwy.ptB.x - uy * perpW} y1={rwy.ptB.y + ux * perpW}
                x2={rwy.ptB.x + uy * perpW} y2={rwy.ptB.y - ux * perpW}
                stroke={recB ? '#16A34A' : '#94A3B8'}
                strokeWidth={recB ? 2.5 : 1.5}
              />
              {/* Label A */}
              <text
                x={lxA} y={lyA}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={compact ? 10 : 12}
                fontWeight="800"
                fill={recA ? '#16A34A' : '#475569'}
              >
                {rwy.runwayA}
              </text>
              {/* Label B */}
              <text
                x={lxB} y={lyB}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={compact ? 10 : 12}
                fontWeight="800"
                fill={recB ? '#16A34A' : '#475569'}
              >
                {rwy.runwayB}
              </text>
            </g>
          );
        })}

        {/* Wind indicator — only after mount to avoid hydration mismatch */}
        {mounted && (
          <g>
            {/* Outer glow ring */}
            <circle
              cx={windCx} cy={windCy} r={compact ? 16 : 20}
              fill="rgba(29,78,216,0.08)"
              stroke="rgba(29,78,216,0.2)"
              strokeWidth="1.5"
            />
            {/* Arrow outer glow */}
            <line
              x1={windCx} y1={windCy}
              x2={wax} y2={way}
              stroke="rgba(29,78,216,0.25)"
              strokeWidth={compact ? 9 : 11}
              strokeLinecap="round"
            />
            {/* Arrow mid glow */}
            <line
              x1={windCx} y1={windCy}
              x2={wax} y2={way}
              stroke="rgba(59,130,246,0.35)"
              strokeWidth={compact ? 6 : 8}
              strokeLinecap="round"
            />
            {/* Arrow main line */}
            <line
              x1={windCx} y1={windCy}
              x2={wax} y2={way}
              stroke="#1D4ED8"
              strokeWidth={compact ? 3 : 4}
              strokeLinecap="round"
            />
            {/* Arrowhead */}
            <polygon
              points={`${wax},${way} ${hx1},${hy1} ${hx2},${hy2}`}
              fill="#1D4ED8"
            />
          </g>
        )}

        {/* "VENT DU xxx°" button — inside the SVG, top-right area */}
        {mounted && (
          <g>
            <rect
              x={ventBtnX - ventBtnW}
              y={ventBtnY - ventBtnH / 2}
              width={ventBtnW}
              height={ventBtnH}
              rx={ventBtnH / 2}
              fill="#DBEAFE"
              stroke="#93C5FD"
              strokeWidth="1"
            />
            {/* Arrow icon */}
            <text
              x={ventBtnX - ventBtnW + 10}
              y={ventBtnY}
              dominantBaseline="central"
              fontSize={ventFontSize + 1}
              fill="#2563EB"
              fontWeight="700"
            >→</text>
            <text
              x={ventBtnX - ventBtnW + 20}
              y={ventBtnY}
              dominantBaseline="central"
              fontSize={ventFontSize}
              fontWeight="800"
              fill="#1D4ED8"
            >
              VENT DU {windDirection}°
            </text>
          </g>
        )}

        {/* North indicator */}
        <text
          x={vbW / 2} y={14}
          textAnchor="middle"
          fontSize="9"
          fontWeight="700"
          fill="#94A3B8"
        >N</text>
        <line
          x1={vbW / 2} y1={17}
          x2={vbW / 2} y2={22}
          stroke="#CBD5E1"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
}